import {
  Card,
  Col,
  DatePicker,
  Flex,
  Form,
  FormProps,
  Input,
  notification,
  Row,
  Select,
  Table,
  TableColumnsType,
  TableProps,
} from 'antd';
import Title from 'antd/es/typography/Title';
import locale from 'antd/es/date-picker/locale/th_TH';
import {
  IBuyerSearchPayment,
  IBuyerSearchPaymentReq,
  IBuyerSearchPaymentRes,
  IPaymentInvoiceList,
} from '@/interfaces/buyer';
import dayjs from 'dayjs';
import { useState } from 'react';
import { formatDate, formatNumber } from '@/utilities/dataUtils';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { paymentEntry, searchBuyerPayment } from '@/adapters/buyer';
import { IServerInfoRes } from '@/interfaces/misc';
import { getServerInfo } from '@/adapters/misc';
import VendorSelector from '@/components/selector/vendor-selector';
import { IVendor } from '@/interfaces/vendor';
import ConfirmApprove from '@/components/modal/confirm-modal';
import RejectModal from '@/components/modal/reject-modal';
import classNames from 'classnames';
import {
  getBuyerPaymentStatusColor,
  getBuyerPaymentStatusName,
  getBuyerPaymentStatusOptions,
} from '@/helpers/buyer';
// import { searchBanks } from '@/adapters/bank';
import PaymentInvoiceModal from './components/payment-invoice-modal';
import { getQueryParam } from '@/utilities/queryUtils';
import { jobProcess } from '@/adapters/job';

const { RangePicker } = DatePicker;

const _criteria: IBuyerSearchPaymentReq = {
  docNo: '',
  dateFrom: dayjs().add(-1, 'month').startOf('month').format('YYYY-MM-DD'),
  dateTo: dayjs().endOf('month').format('YYYY-MM-DD'),
  pageStart: 1,
  pageSize: -1,
  vendor: undefined,
  status: '',
};

const PaymentPage: React.FC = () => {
  const job = getQueryParam('job');
  const [form] = Form.useForm();
  const [criteria, setCriteria] = useState<IBuyerSearchPaymentReq>({
    ..._criteria,
  });
  const [confirm, setConfirm] = useState<{
    open: boolean;
    item?: IBuyerSearchPayment;
  }>({
    open: false,
  });

  const [reject, setReject] = useState<{
    open: boolean;
    item?: IBuyerSearchPayment;
  }>({
    open: false,
  });

  const [selectedRowKey, setSelectedRowKey] = useState<IBuyerSearchPayment[]>(
    []
  );

  const [invoiceModal, setInvoiceModal] = useState<{
    open: boolean;
    invoiceList: IPaymentInvoiceList[];
  }>({
    open: false,
    invoiceList: [],
  });

  const formItemLayout = {
    labelCol: {
      xs: { span: 24 },
      sm: { span: 6 },
    },
    wrapperCol: {
      xs: { span: 24 },
      sm: { span: 14 },
    },
  };

  const onFinish: FormProps<IBuyerSearchPaymentReq>['onFinish'] = (values) => {
    console.log('Success:', values);
    setCriteria({
      ...criteria,
      docNo: values.docNo,
      dateFrom: values.docDate?.[0].format('YYYY-MM-DD') || undefined,
      dateTo: values.docDate?.[1].format('YYYY-MM-DD') || undefined,
      vendor: values.vendor,
      status: values.status,
      // docStatus: values.docStatus,
    });
  };

  const onClear = () => {
    form.setFieldsValue({
      ..._criteria,
      docDate: [dayjs(_criteria.dateFrom), dayjs(_criteria.dateTo)],
    });
    form.submit();
  };

  const columns: TableColumnsType<IBuyerSearchPayment> = [
    {
      title: 'ลำดับ',
      dataIndex: '',
      width: 60,
      align: 'center',
      className: 'text-bold',
      render: (_, rec, i) => <div className="font-weight-bold">{i + 1}</div>,
    },
    {
      title: 'ใบคลุมจ่าย',
      dataIndex: 'PAYMENT_CODE',
      align: 'center',
      className: 'text-bold',
      width: 120,
      fixed: 'left',
    },
    {
      title: 'เอกสารจ่าย',
      dataIndex: 'AP_REF',
      align: 'center',
      className: 'text-bold',
      width: 120,
      fixed: 'left',
    },
    {
      title: 'วันที่จ่าย',
      dataIndex: 'AP_DATE',
      align: 'center',
      className: 'text-bold',
      width: 120,
      render: (_) => formatDate(_),
    },
    {
      title: 'เจ้าหนี้',
      dataIndex: 'VENDOR',
      align: 'center',
      className: 'text-bold',
      width: 120,
    },
    {
      title: 'ชื่อเจ้าหนี้',
      dataIndex: 'VENDOR_NAME',
      className: 'text-bold',
    },
    {
      title: 'เครดิตเทอม',
      dataIndex: 'AP_CT_TERMS',
      align: 'center',
      className: 'text-bold',
      width: 120,
    },
    {
      title: 'ยอดเงิน',
      dataIndex: 'CKH_APPLIED_AMT',
      align: 'right',
      width: 120,
      className: 'text-bold text-primary',
      render: (_) => formatNumber(_, 2),
    },
    {
      title: 'สถานะเอกสาร',
      dataIndex: 'MESSAGE_STATUS',
      align: 'center',
      width: 120,
      render: (_) => (
        <div className={`${classNames(getBuyerPaymentStatusColor(_))} rounded`}>
          {getBuyerPaymentStatusName(_)}
        </div>
      ),
    },
    {
      title: '',
      dataIndex: 'AP_STATUS',
      align: 'center',
      width: 180,
      className: 'text-bold',
      fixed: 'right',
      render: (_, rec) => (
        <div>
          {rec.AP_STATUS === 'PDNG' && (
            <button
              type="button"
              className="btn btn-danger btn-sm"
              style={{ minWidth: 80 }}
              onClick={() => {
                setReject({
                  open: true,
                  item: rec,
                });
              }}
            >
              ยกเลิกการจ่ายเงิน
            </button>
          )}
        </div>
      ),
    },
    {
      title: '',
      dataIndex: 'InvoiceList',
      align: 'center',
      width: 120,
      render: (_) => (
        <div>
          {
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={() => {
                setInvoiceModal({
                  invoiceList: _,
                  open: true,
                });
              }}
            >
              invoice list
            </button>
          }
        </div>
      ),
    },
  ];

  const { data: payments, refetch } = useQuery<
    AxiosResponse<IBuyerSearchPaymentRes>,
    Error
  >({
    queryKey: ['search-buyer-payment-inquiry', criteria],
    queryFn: async () => await searchBuyerPayment(criteria),
    // enabled: !!criteria.vendor,
  });

  const { data: serverInfo } = useQuery<AxiosResponse<IServerInfoRes>, Error>({
    queryKey: ['search-server-info', criteria],
    queryFn: async () => await getServerInfo(),
  });

  // const { data: banks } = useQuery<AxiosResponse<any>, Error>({
  //   queryKey: ['search-banks'],
  //   queryFn: async () => await searchBanks(),
  // });

  const mutations = useMutation({
    mutationFn: async (data: Partial<IBuyerSearchPayment>) =>
      await paymentEntry(data),
    mutationKey: ['payment-entry'],
    onSuccess: (data) => {
      if (data.status === 200) {
        notification.success({
          message: 'ส่งเอกสารสำเร็จ',
        });
        refetch();
        setConfirm({ open: false, item: undefined });
        setReject({ open: false, item: undefined });
      } else {
      }
    },
    onError: (error) => {
      console.log('error', error);
    },
  });

  function onEntry(): void {
    mutations.mutateAsync({ ...selectedRowKey[0] });
  }

  function onConfirm(): void {
    // mutations.mutateAsync({
    //   // acceptance: 'ACPT',
    //   ...confirm.item,
    // });
  }

  function onReject(error: string): void {
    // mutations.mutateAsync({
    //   // acceptance: 'RJCT',
    //   ...reject.item,
    //   // AR_REMARK: error,
    // });
  }

  const rowSelection: TableProps<IBuyerSearchPayment>['rowSelection'] = {
    onChange: (
      selectedRowKeys: React.Key[],
      selectedRows: IBuyerSearchPayment[]
    ) => {
      setSelectedRowKey(selectedRows);
    },
    getCheckboxProps: (record: IBuyerSearchPayment) => ({
      disabled: ![''].includes(record.AP_STATUS || ''),
    }),
    selectedRowKeys: selectedRowKey.map((c) => c.AP_REF) || [],
  };

  return (
    <>
      <Flex justify="space-between" gap={2}>
        <div>
          <Title level={2} className="text-center">
            {serverInfo?.data?.data?.[0]?.SERVER_NAME}
          </Title>
        </div>
        <div>
          <Title
            level={2}
            className="text-center"
            onClick={() => (job === 'x12' ? jobProcess('payment') : undefined)}
          >
            ค้นหาเอกสารจ่าย
          </Title>
        </div>
      </Flex>
      <Card>
        <Form
          {...formItemLayout}
          layout={'horizontal'}
          form={form}
          initialValues={{
            ...criteria,
            docDate: [dayjs(criteria.dateFrom), dayjs(criteria.dateTo)],
          }}
          onFinish={onFinish}
          autoComplete="off"
          labelAlign={'left'}
        >
          <Row gutter={24}>
            <Col md={12}>
              <Form.Item name="docNo" label="เลขที่เอกสาร">
                <Input placeholder="เลขที่เอกสาร" size="large" />
              </Form.Item>
            </Col>
            <Col md={12}>
              <Form.Item name={'docDate'} label="วันที่เอกสาร">
                <RangePicker
                  size="large"
                  style={{ width: '100%' }}
                  locale={locale}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col md={12}>
              <Form.Item<IBuyerSearchPaymentReq> name="vendor" label="เจ้าหนี้">
                <VendorSelector
                  handleSelectData={function (vendor: IVendor): void {
                    form.setFieldsValue({
                      vendor: vendor?.VDAD_ID,
                    });
                  }}
                />
              </Form.Item>
            </Col>
            <Col md={12}>
              <Form.Item<IBuyerSearchPaymentReq> name="status" label="สถานะ">
                <Select
                  showSearch
                  placeholder="เลือกสถานะ"
                  optionFilterProp="label"
                  size="large"
                  defaultValue={criteria?.status}
                  options={[...getBuyerPaymentStatusOptions()]}
                />
              </Form.Item>
            </Col>
          </Row>
          <Flex gap={24} justify="center">
            <button
              type="button"
              className="btn btn-success"
              style={{ minWidth: 150 }}
              disabled={!selectedRowKey?.length}
              onClick={() => onEntry()}
            >
              {mutations.isPending ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  <span className="sr-only">Loading...</span>
                </>
              ) : (
                'ส่งเอกสาร '
              )}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              style={{ minWidth: 150 }}
            >
              ค้นหา
            </button>
            <button
              type="button"
              className="btn btn-danger"
              style={{ minWidth: 150 }}
              onClick={onClear}
            >
              ยกเลิก
            </button>
          </Flex>
        </Form>
      </Card>
      <Card className="mt-3">
        <Table<IBuyerSearchPayment>
          rowSelection={{ type: 'radio', ...rowSelection }}
          columns={columns}
          dataSource={payments?.data?.data || []}
          size="small"
          bordered
          rowKey={(c) => c.AP_REF}
          scroll={{ x: 1400 }}
          pagination={false}
        />
      </Card>
      <ConfirmApprove
        open={confirm.open}
        title={'ยืนการส่งเอกสาร'}
        content={'ยืนยันการส่งเอกสาร'}
        handleOk={function (): void {
          onConfirm();
        }}
        handleCancel={function (): void {
          setConfirm({
            open: false,
            item: undefined,
          });
        }}
      />
      <RejectModal
        open={reject.open}
        title={'ยืนการส่งเอกสาร'}
        content={'ปฏิเสธการส่งเอกสาร'}
        handleOk={function (err): void {
          onReject(err);
        }}
        handleCancel={function (): void {
          setReject({
            open: false,
            item: undefined,
          });
        }}
      />
      <PaymentInvoiceModal
        open={invoiceModal?.open || false}
        invoiceList={invoiceModal.invoiceList || []}
        onClose={function (): void {
          setInvoiceModal({
            invoiceList: [],
            open: false,
          });
        }}
      />
    </>
  );
};

export default PaymentPage;
